import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import Fuse from 'fuse.js';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { DriverShiftOverviewResponse, RevenueAllocationItem, RevenueAllocationResponse } from 'src/app/types';
import { fuse } from 'src/app/utilities/fuse';
import { GraphDataInfo, RevenueAllocationService } from '../revenue-allocation.service';
import { ShiftService } from '../shift.service';

const searchOptions: Fuse.IFuseOptions<RevenueAllocationItem> = {
  distance: 100,
  keys: ['name'],
  location: 0,
  minMatchCharLength: 1,
  shouldSort: true,
  threshold: 0.2,
};

@Component({
  selector: 'lohi-revenue-allocation-dashboard',
  templateUrl: './revenue-allocation-dashboard.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RevenueAllocationDashboardComponent implements OnInit, OnDestroy {
  private destroy$$ = new Subject();
  public regions$: Observable<DriverShiftOverviewResponse>;
  public searchForm = new FormControl(null);
  public filter$$ = new BehaviorSubject<'ALL' | 'PRIORITY' | 'FLOATING'>('ALL');
  public graphDataInfo$: Observable<GraphDataInfo>;
  public graphData$: Observable<RevenueAllocationResponse>;
  public items$: Observable<RevenueAllocationItem[]>;
  constructor(private revenueAllocationService: RevenueAllocationService, private shiftService: ShiftService) {}

  public ngOnInit(): void {
    this.regions$ = this.shiftService.driversOnShift$;
    this.graphDataInfo$ = this.revenueAllocationService.graphDataInfo$;
    this.graphData$ = this.revenueAllocationService.graphData$;
    this.items$ = combineLatest([
      this.searchForm.valueChanges.pipe(startWith(null as any)),
      this.filter$$,
      this.graphDataInfo$.pipe(
        map((info) => info?.revenueAllocationItems || []),
        map(fuse(searchOptions)),
      ),
    ]).pipe(
      map(([searchTerm, filter, itemsFused]) => {
        const items = searchTerm ? itemsFused.fuse.search(searchTerm).map((i) => i.item) : itemsFused.data;
        if (filter === 'FLOATING') {
          return items.filter((item) => !item.isPriority);
        }
        if (filter === 'PRIORITY') {
          return items.filter((item) => item.isPriority);
        }
        return items;
      }),
    );
  }

  public ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.unsubscribe();
  }

  public setTarget(carrierId: string, targetDollars: number) {
    if (targetDollars === 0) {
      return;
    }
    this.revenueAllocationService.setTarget(carrierId, targetDollars * 100);
  }

  public setRegion(regionId: number) {
    this.revenueAllocationService.setRegion(regionId);
  }

  public setDaysToLookAhead(daysToLookAhead: number) {
    this.revenueAllocationService.setDaysToLookAhead(daysToLookAhead);
  }

  public saveData() {
    this.revenueAllocationService.saveData();
  }

  public maybeUnprioritize(carrierId: string, checked: boolean) {
    if (!checked) {
      this.revenueAllocationService.setTarget(carrierId, null);
    }
  }
}
